import React, { useEffect, useRef } from 'react';
import { useApp } from '../../contexts/AppContext';
import { LoadingSpinner, Logo } from '../Common';
import TypingIndicator from './TypingIndicator';
import './ChatArea.css';

const ChatArea: React.FC = () => {
  const { state } = useApp();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Get the correct messages based on current mode
  const currentMessages = state.chatMode === 'chatbot' ? state.chatbotMessages : state.voicebotMessages;

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    const scrollToBottom = () => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    };

    // Small delay to ensure DOM is updated
    const timeoutId = setTimeout(scrollToBottom, 100);
    
    return () => clearTimeout(timeoutId);
  }, [currentMessages, state.isProcessing]);

  if (currentMessages.length === 0) {
    return (
      <div className="chat-area">
        <div className="empty-chat">
          <div className="empty-chat-icon">
            <Logo size={48} />
          </div>
          <p className="empty-chat-text">
            {state.chatMode === 'chatbot' 
              ? 'Type a message to start conversation' 
              : 'Click mic button to start conversation'
            }
          </p>
          {state.chatMode === 'chatbot' && state.isProcessing && (
            <TypingIndicator />
          )}
          {/* Invisible element to scroll to */}
          <div ref={messagesEndRef} />
        </div>
      </div>
    );
  }

  return (
    <div className="chat-area">
      <div className="messages-container">
        {currentMessages.map((message) => (
          <div
            key={message.id}
            className={`message ${message.sender === 'user' ? 'user-message' : 'assistant-message'}`}
          >
            <div className="message-content">
              <div className="message-text">{message.content}</div>
              <div className="message-meta">
                <span className="message-time">
                  {message.timestamp.toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </span>
                {message.type === 'voice' && (
                  <span className="message-type">🎤 Voice</span>
                )}
              </div>
            </div>
          </div>
        ))}
        {state.isProcessing && (
          <div className="message assistant-message">
            <div className="message-content">
              <div className="processing-indicator">
                <LoadingSpinner size="small" />
                <span>Processing...</span>
              </div>
            </div>
          </div>
        )}
        {/* Invisible element to scroll to */}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default ChatArea;
